import { useState, useEffect, useCallback } from 'react';
import { Upload, Button, Progress, List, Space, Tag, Typography } from 'antd';
import { 
  InboxOutlined, 
  PauseCircleOutlined, 
  PlayCircleOutlined, 
  CloseCircleOutlined,
  FileOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useUpload } from '../hooks/useUpload';
import { formatFileSize, isFileTypeAllowed } from '../utils/format';
import type { UploadTask } from '../types';

const { Dragger } = Upload;
const { Text } = Typography;

interface FileUploadProps {
  onAllUploadsComplete?: (allSucceeded: boolean) => void;
  colors: {
    primary: string;
    success: string;
    text: string;
    textMuted: string;
    border: string;
    surface: string;
    background: string;
    hover: string;
  };
}

const STATUS_CONFIG: Record<UploadTask['status'], { text: string; color: string; icon: any }> = {
  pending: { text: '等待中', color: '#9CA3AF', icon: null },
  uploading: { text: '上传中', color: '#000000', icon: null },
  paused: { text: '已暂停', color: '#F59E0B', icon: null },
  completed: { text: '已完成', color: '#10B981', icon: CheckCircleOutlined },
  error: { text: '失败', color: '#EF4444', icon: null },
};

export default function FileUpload({ onAllUploadsComplete, colors }: FileUploadProps) {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { 
    uploadTasks, 
    addUploadTasks, 
    pauseUpload, 
    resumeUpload, 
    cancelUpload, 
    completedCount,
    uploadSpeeds,
    formatSpeed,
    formatRemainingTime,
  } = useUpload(onAllUploadsComplete);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const validFiles: File[] = [];
    const rejectedFiles: string[] = [];

    Array.from(files).forEach(file => {
      if (isFileTypeAllowed(file.name, file.type)) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(file.name);
      }
    });

    if (rejectedFiles.length > 0) {
      import('antd').then(({ message }) => {
        message.warning(`${rejectedFiles.join(', ')} 是禁止上传的文件类型`);
      });
    }

    if (validFiles.length > 0) {
      // 累积文件而不是立即上传
      setPendingFiles((prev: File[]) => [...prev, ...validFiles]);
    }
  }, []);

  // 处理累积的文件
  useEffect(() => {
    if (pendingFiles.length > 0 && !isProcessing) {
      setIsProcessing(true);
      addUploadTasks(pendingFiles);
      setPendingFiles([]);
      setIsProcessing(false);
    }
  }, [pendingFiles, isProcessing, addUploadTasks]);

  return (
    <div>
      {/* Upload Area */}
      {uploadTasks.length === 0 ? (
        <Dragger
          multiple
          showUploadList={false}
          beforeUpload={(file) => {
            handleFileSelect([file] as unknown as FileList);
            return false;
          }}
          onDrop={(e) => {
            if (e.dataTransfer.files.length > 0) {
              handleFileSelect(e.dataTransfer.files);
            }
          }}
          style={{
            padding: '48px 20px',
            background: colors.background,
            border: `2px dashed ${colors.border}`,
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <InboxOutlined style={{ fontSize: 48, color: colors.textMuted, marginBottom: 16 }} />
            <p style={{ fontSize: 16, fontWeight: 500, color: colors.text, marginBottom: 8 }}>
              点击或拖拽文件到此处上传
            </p>
            <p style={{ fontSize: 14, color: colors.textMuted }}>
              支持批量上传，禁止上传 HTML 文件
            </p>
          </div>
        </Dragger>
      ) : (
        <>
          {/* Add More Files Button */}
          <div style={{ marginBottom: 16 }}>
            <Dragger
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                handleFileSelect([file] as unknown as FileList);
                return false;
              }}
              style={{
                padding: '12px',
                background: colors.background,
                border: `2px dashed ${colors.border}`,
                borderRadius: 6,
              }}
            >
              <Text style={{ color: colors.text }}>+ 继续添加文件</Text>
            </Dragger>
          </div>

          {/* Upload Tasks List */}
          <List
            dataSource={uploadTasks}
            renderItem={(task) => {
              const statusConfig = STATUS_CONFIG[task.status];
              const StatusIcon = statusConfig.icon;
              const speedInfo = uploadSpeeds[task.id];

              return (
                <List.Item
                  style={{
                    padding: '16px',
                    marginBottom: 12,
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                  }}
                  actions={[
                    task.status === 'uploading' && (
                      <Button
                        key="pause"
                        type="text"
                        icon={<PauseCircleOutlined />}
                        onClick={() => pauseUpload(task.id)}
                        style={{ color: colors.textMuted }}
                      />
                    ),
                    task.status === 'paused' && (
                      <Button
                        key="resume"
                        type="text"
                        icon={<PlayCircleOutlined />}
                        onClick={() => resumeUpload(task.id)}
                        style={{ color: colors.primary }}
                      />
                    ),
                    task.status !== 'completed' && (
                      <Button
                        key="cancel"
                        type="text"
                        icon={<CloseCircleOutlined />}
                        onClick={() => cancelUpload(task.id)}
                        style={{ color: '#EF4444' }}
                      />
                    ),
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ 
                        width: 40, 
                        height: 40, 
                        background: colors.background,
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <FileOutlined style={{ fontSize: 20, color: colors.textMuted }} />
                      </div>
                    }
                    title={
                      <Space>
                        <Text strong style={{ color: colors.text }}>
                          {task.fileName}
                        </Text>
                        <Tag 
                          style={{ 
                            background: statusConfig.color + '20', 
                            color: statusConfig.color,
                            border: 'none',
                            fontWeight: 500 
                          }}
                        >
                          {StatusIcon && <StatusIcon style={{ marginRight: 4 }} />}
                          {statusConfig.text}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <Space split={<Text type="secondary">|</Text>}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {formatFileSize(task.fileSize)}
                          </Text>
                          {/* 上传速度和剩余时间 */}
                          {task.status === 'uploading' && speedInfo && (
                            <>
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                <ThunderboltOutlined style={{ marginRight: 4 }} />
                                {formatSpeed(speedInfo.speed)}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                剩余 {formatRemainingTime(speedInfo.remainingTime)}
                              </Text>
                            </>
                          )}
                          {/* 断点续传提示 */}
                          {task.status === 'paused' && task.completedParts && task.parts && (
                            <Text type="warning" style={{ fontSize: 13 }}>
                              已上传 {task.completedParts}/{task.parts.length} 分片
                            </Text>
                          )}
                        </Space>
                        {(task.status === 'uploading' || task.status === 'pending') && (
                          <Progress
                            percent={task.progress}
                            status="active"
                            size="small"
                            strokeColor={colors.primary}
                            trailColor={colors.border}
                          />
                        )}
                        {task.error && (
                          <Text type="danger" style={{ fontSize: 13 }}>
                            {task.error}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />

          {/* Summary */}
          {completedCount > 0 && (
            <div style={{ 
              marginTop: 16, 
              padding: 16, 
              background: colors.background,
              borderRadius: 6,
              textAlign: 'center'
            }}>
              <Text style={{ color: colors.success, fontWeight: 600 }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                已完成 {completedCount} 个文件上传
              </Text>
            </div>
          )}
        </>
      )}
    </div>
  );
}
